<template>
  <section class="geo-map-report">
    <b-card no-body class="mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div>
            <h4 class="mb-0">Client Geo Map</h4>
            <small class="text-muted">PPP status from MikroTik with customer locations.</small>
          </div>
          <div class="d-flex flex-wrap align-items-center">
            <b-button
              variant="outline-primary"
              class="mr-1 mb-50"
              :disabled="isLoading || !filteredItems.length"
              @click="fitToMarkers"
            >
              <feather-icon icon="MapPinIcon" size="16" class="mr-50" />
              Fit to points
            </b-button>
            <b-button variant="primary" class="mb-50" :disabled="isLoading" @click="fetchMapData">
              Refresh
            </b-button>
          </div>
        </div>

        <b-row class="mt-2">
          <b-col v-for="card in summaryCards" :key="card.label" md="3" sm="6" class="mb-1">
            <div class="d-flex align-items-center report-kpi-card">
              <b-avatar size="44" :variant="card.variant" class="mr-1">
                <feather-icon :icon="card.icon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ card.value }}</div>
                <small class="text-muted">{{ card.label }}</small>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-row>
      <b-col lg="4" class="mb-2">
        <b-card>
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h5 class="mb-0">Filters</h5>
            <b-badge variant="light-primary">{{ filteredItems.length }} items</b-badge>
          </div>

          <b-form-group label="Search">
            <b-input-group>
              <b-form-input
                v-model="searchTerm"
                placeholder="Search username or name"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="!searchTerm"
                  @click="clearSearch"
                >
                  Clear
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-form-group label="PPP Status">
            <b-button-group size="sm" class="geo-status-group">
              <b-button
                :variant="statusFilter === 'all' ? 'primary' : 'outline-primary'"
                @click="statusFilter = 'all'"
              >
                All
              </b-button>
              <b-button
                :variant="statusFilter === 'online' ? 'success' : 'outline-success'"
                @click="statusFilter = 'online'"
              >
                Online
              </b-button>
              <b-button
                :variant="statusFilter === 'offline' ? 'danger' : 'outline-danger'"
                @click="statusFilter = 'offline'"
              >
                Offline
              </b-button>
              <b-button
                :variant="statusFilter === 'unknown' ? 'secondary' : 'outline-secondary'"
                @click="statusFilter = 'unknown'"
              >
                Unknown
              </b-button>
            </b-button-group>
          </b-form-group>

          <b-form-checkbox v-model="useBbox" switch class="mb-1">
            Limit to map view (bbox)
          </b-form-checkbox>
          <small class="text-muted d-block mb-1">
            When ON, moving the map auto-refreshes results.
          </small>

          <b-row>
            <b-col cols="6">
              <b-form-group label="Sort list by" label-class="text-muted">
                <b-form-select v-model="sortKey" :options="sortOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group label="List size" label-class="text-muted">
                <b-form-select v-model="listLimit" :options="listLimitOptions" size="sm" />
              </b-form-group>
            </b-col>
          </b-row>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="mb-1"
            :disabled="!canReset"
            @click="resetFilters"
          >
            Reset filters
          </b-button>

          <b-alert v-if="loadError" variant="danger" show class="mb-2">
            {{ loadError }}
          </b-alert>

          <div v-if="isLoading" class="text-center text-muted py-2">
            <b-spinner small class="mr-50" />
            Loading locations...
          </div>

          <div v-else class="geo-list">
            <div v-if="!filteredItems.length" class="text-center text-muted py-2">
              No locations found.
            </div>
            <div
              v-for="item in listItems"
              :key="item.id"
              class="geo-list-item"
            >
              <span :class="['geo-dot', statusDotClass(item)]"></span>
              <div class="geo-list-content">
                <div class="font-weight-bolder">{{ item.user_name }}</div>
                <small class="text-muted">{{ item.title || 'Unknown location' }}</small>
              </div>
              <small class="text-muted">{{ statusLabel(item) }}</small>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col lg="8">
        <b-card no-body>
          <div class="geo-map-wrapper">
            <l-map
              ref="geoMap"
              class="geo-map"
              :zoom="mapZoom"
              :center="mapCenter"
              @ready="onMapReady"
              @update:zoom="mapZoom = $event"
              @update:center="mapCenter = $event"
            >
              <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />
              <l-marker
                v-for="item in filteredItems"
                :key="item.id"
                :lat-lng="[item.lat, item.lng]"
                :icon="resolveMarkerIcon(item)"
              >
                <l-tooltip>{{ item.user_name }}</l-tooltip>
                <l-popup>
                  <div class="geo-popup">
                    <div class="font-weight-bolder">{{ item.user_name }}</div>
                    <div class="text-muted">{{ item.title || 'Unknown location' }}</div>
                    <div class="mt-50">
                      <span class="text-muted">PPP:</span>
                      <strong>{{ statusLabel(item) }}</strong>
                    </div>
                    <div class="mt-25">
                      <span class="text-muted">Secret:</span>
                      <strong>{{ secretLabel(item) }}</strong>
                    </div>
                    <div class="mt-25">
                      <span class="text-muted">Last logout:</span>
                      <strong>{{ item.ppp_last_logged_out || 'N/A' }}</strong>
                    </div>
                    <div class="mt-25">
                      <span class="text-muted">Last app hit:</span>
                      <strong>{{ formatDate(item.last_app_hit) }}</strong>
                    </div>
                  </div>
                </l-popup>
              </l-marker>
            </l-map>
            <div v-if="isLoading" class="geo-map-loading">
              <b-spinner />
              <div class="text-muted mt-50">Loading map...</div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BButtonGroup,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BInputGroup,
  BInputGroupAppend,
  BAvatar,
  BBadge,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import { LMap, LTileLayer, LMarker, LPopup, LTooltip } from 'vue2-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { GEO_MAP_REPORT } from '@core/services/api'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BInputGroup,
    BInputGroupAppend,
    BAvatar,
    BBadge,
    BAlert,
    BSpinner,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LTooltip,
  },
  data() {
    return {
      items: [],
      isLoading: false,
      loadError: null,
      searchTerm: '',
      statusFilter: 'all',
      useBbox: true,
      sortKey: 'status',
      sortOptions: [
        { value: 'status', text: 'Status' },
        { value: 'name', text: 'Username' },
        { value: 'last_hit', text: 'Last App Hit' },
      ],
      listLimit: 200,
      listLimitOptions: [
        { value: 50, text: '50' },
        { value: 100, text: '100' },
        { value: 200, text: '200' },
        { value: 500, text: '500' },
      ],
      mapCenter: [23.685, 90.3563],
      mapZoom: 7,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttribution: '&copy; OpenStreetMap contributors',
      markerIcons: {},
      mapReady: false,
      bboxFetchTimer: null,
      lastBboxKey: null,
      isAutoFitting: false,
    }
  },
  computed: {
    filteredItems() {
      let rows = (this.items || []).filter(item => Number.isFinite(item.lat) && Number.isFinite(item.lng))
      if (this.statusFilter === 'online') {
        rows = rows.filter(item => item.ppp_active === true)
      } else if (this.statusFilter === 'offline') {
        rows = rows.filter(item => item.ppp_active === false)
      } else if (this.statusFilter === 'unknown') {
        rows = rows.filter(item => item.ppp_active === null || item.ppp_active === undefined)
      }
      if (this.searchTerm) {
        const term = this.searchTerm.trim().toLowerCase()
        rows = rows.filter(item => {
          const name = (item.user_name || '').toLowerCase()
          const title = (item.title || '').toLowerCase()
          return name.includes(term) || title.includes(term)
        })
      }
      return rows
    },
    sortedItems() {
      const rows = [...this.filteredItems]
      const byName = (a, b) => (a.user_name || '').localeCompare(b.user_name || '')
      if (this.sortKey === 'name') {
        return rows.sort(byName)
      }
      if (this.sortKey === 'last_hit') {
        return rows.sort((a, b) => (b.last_app_hit || '').localeCompare(a.last_app_hit || ''))
      }
      const weight = item => {
        if (item.ppp_active === true) return 0
        if (item.ppp_active === false) return 1
        return 2
      }
      return rows.sort((a, b) => {
        const diff = weight(a) - weight(b)
        if (diff !== 0) return diff
        return byName(a, b)
      })
    },
    listItems() {
      return this.sortedItems.slice(0, this.listLimit)
    },
    canReset() {
      return (
        this.searchTerm ||
        this.statusFilter !== 'all' ||
        this.useBbox !== true ||
        this.sortKey !== 'status' ||
        this.listLimit !== 200
      )
    },
    summaryCards() {
      const counts = this.items.reduce(
        (acc, item) => {
          if (item.ppp_active === true) acc.online += 1
          else if (item.ppp_active === false) acc.offline += 1
          else acc.unknown += 1
          return acc
        },
        { online: 0, offline: 0, unknown: 0 }
      )
      return [
        { label: 'Total', value: this.items.length, icon: 'UsersIcon', variant: 'light-primary' },
        { label: 'Online', value: counts.online, icon: 'WifiIcon', variant: 'light-success' },
        { label: 'Offline', value: counts.offline, icon: 'SlashIcon', variant: 'light-danger' },
        { label: 'Unknown', value: counts.unknown, icon: 'HelpCircleIcon', variant: 'light-secondary' },
      ]
    },
  },
  created() {
    this.markerIcons = {
      online: this.createMarkerIcon('online'),
      offline: this.createMarkerIcon('offline'),
      unknown: this.createMarkerIcon('unknown'),
    }
  },
  mounted() {
    // Fetch after map ready so bbox can be applied.
  },
  beforeDestroy() {
    this.detachMapListeners()
    if (this.bboxFetchTimer) {
      clearTimeout(this.bboxFetchTimer)
      this.bboxFetchTimer = null
    }
  },
  watch: {
    statusFilter() {
      if (this.mapReady) {
        this.fetchMapData({ resetBboxKey: true })
      }
    },
    useBbox() {
      if (this.mapReady) {
        this.fetchMapData({ resetBboxKey: true })
      }
    },
  },
  methods: {
    fetchMapData(options = {}) {
      if (this.useBbox && !this.mapReady) {
        return
      }
      this.isLoading = true
      this.loadError = null
      const params = {}
      if (this.statusFilter === 'online' || this.statusFilter === 'offline') {
        params.status = this.statusFilter
      }
      if (this.useBbox) {
        const bounds = this.getMapBounds()
        if (!bounds) {
          this.isLoading = false
          return
        }
        const bboxKey = this.buildBboxKey(bounds)
        if (!options.resetBboxKey && bboxKey === this.lastBboxKey && options.source === 'bbox') {
          this.isLoading = false
          return
        }
        this.lastBboxKey = bboxKey
        params.lat_min = bounds.getSouth()
        params.lat_max = bounds.getNorth()
        params.lng_min = bounds.getWest()
        params.lng_max = bounds.getEast()
      }
      this.$http
        .get(process.env.VUE_APP_BASEURL + GEO_MAP_REPORT, { params })
        .then(res => {
          const data = res.data && res.data.data ? res.data.data : {}
          this.items = Array.isArray(data.items) ? data.items : []
          if (this.items.length && !this.useBbox) {
            this.updateMapCenter()
          }
        })
        .catch(error => {
          this.loadError = 'Failed to load geo map data.'
          if (error && error.response && error.response.data && error.response.data.messages) {
            this.loadError = error.response.data.messages[0] || this.loadError
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateMapCenter() {
      const first = this.items[0]
      if (first && typeof first.lat === 'number' && typeof first.lng === 'number') {
        this.mapCenter = [first.lat, first.lng]
        this.mapZoom = 10
      }
    },
    fitToMarkers() {
      if (!this.$refs.geoMap || !this.filteredItems.length) return
      const map = this.$refs.geoMap.mapObject
      if (!map) return
      this.isAutoFitting = true
      const bounds = this.filteredItems.map(item => [item.lat, item.lng])
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 })
      setTimeout(() => {
        this.isAutoFitting = false
      }, 600)
    },
    resolveMarkerIcon(item) {
      if (item.ppp_active === true) return this.markerIcons.online
      if (item.ppp_active === false) return this.markerIcons.offline
      return this.markerIcons.unknown
    },
    statusLabel(item) {
      if (item.ppp_active === true) return 'Online'
      if (item.ppp_active === false) return 'Offline'
      return 'Unknown'
    },
    statusDotClass(item) {
      if (item.ppp_active === true) return 'geo-dot--online'
      if (item.ppp_active === false) return 'geo-dot--offline'
      return 'geo-dot--unknown'
    },
    secretLabel(item) {
      if (item.ppp_secret_disabled === true) return 'Disabled'
      if (item.ppp_secret_disabled === false) return 'Enabled'
      return 'Unknown'
    },
    formatDate(value) {
      if (!value) return 'N/A'
      return value
    },
    createMarkerIcon(status) {
      return L.divIcon({
        className: `geo-marker geo-marker--${status}`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -9],
        html: '<span class="geo-marker__dot"></span>',
      })
    },
    onMapReady() {
      this.mapReady = true
      this.attachMapListeners()
      this.fetchMapData({ resetBboxKey: true })
    },
    attachMapListeners() {
      const map = this.$refs.geoMap && this.$refs.geoMap.mapObject
      if (!map) return
      map.on('moveend', this.handleMapMove)
      map.on('zoomend', this.handleMapMove)
    },
    detachMapListeners() {
      const map = this.$refs.geoMap && this.$refs.geoMap.mapObject
      if (!map) return
      map.off('moveend', this.handleMapMove)
      map.off('zoomend', this.handleMapMove)
    },
    handleMapMove() {
      if (!this.useBbox || this.isAutoFitting) return
      this.scheduleBboxFetch()
    },
    scheduleBboxFetch() {
      if (this.bboxFetchTimer) {
        clearTimeout(this.bboxFetchTimer)
      }
      this.bboxFetchTimer = setTimeout(() => {
        this.fetchMapData({ source: 'bbox' })
      }, 500)
    },
    getMapBounds() {
      const map = this.$refs.geoMap && this.$refs.geoMap.mapObject
      if (!map) return null
      return map.getBounds()
    },
    buildBboxKey(bounds) {
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      const parts = [
        sw.lat.toFixed(4),
        sw.lng.toFixed(4),
        ne.lat.toFixed(4),
        ne.lng.toFixed(4),
        this.statusFilter,
        this.useBbox ? '1' : '0',
      ]
      return parts.join('|')
    },
    clearSearch() {
      this.searchTerm = ''
    },
    resetFilters() {
      this.searchTerm = ''
      this.statusFilter = 'all'
      this.useBbox = true
      this.sortKey = 'status'
      this.listLimit = 200
      if (this.mapReady) {
        this.fetchMapData({ resetBboxKey: true })
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/base/plugins/maps/map-leaflet.scss";

.geo-map-report {
  .report-kpi-card {
    background: #f8fafc;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 0.6rem;
    padding: 0.75rem;
  }

  .geo-map-wrapper {
    position: relative;
    min-height: 420px;
  }

  .geo-map {
    height: 70vh;
    min-height: 420px;
  }

  .geo-map-loading {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.75);
    z-index: 401;
    font-weight: 500;
  }

  .geo-list {
    max-height: 420px;
    overflow: auto;
  }

  .geo-status-group {
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .geo-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 0;
    border-bottom: 1px solid rgba(34, 41, 47, 0.08);
    gap: 0.75rem;
  }

  .geo-list-content {
    flex: 1;
  }

  .geo-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }

  .geo-dot--online {
    background-color: #28c76f;
  }

  .geo-dot--offline {
    background-color: #ea5455;
  }

  .geo-dot--unknown {
    background-color: #b9c3cd;
  }

  .geo-popup {
    min-width: 180px;
  }
}

.geo-marker {
  width: 18px;
  height: 18px;
}

.geo-marker__dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  display: block;
  box-shadow: 0 0 0 2px rgba(34, 41, 47, 0.15);
}

.geo-marker--online .geo-marker__dot {
  background-color: #28c76f;
}

.geo-marker--offline .geo-marker__dot {
  background-color: #ea5455;
}

.geo-marker--unknown .geo-marker__dot {
  background-color: #b9c3cd;
}
</style>
